import { useApiAdmininsightspointsGetCollection } from '@r/api/generated/client.ts';
import {
  type FormattedDataForRendering,
  BarChartBloc
} from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { DataBrickSkeleton } from '@r/components/kpi/skeleton/data-brick-skeleton.tsx';
import { CoinsIcon } from 'lucide-react';
import pointsTranslation from '@r/translations/points-translation.ts';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { BarChartSkeleton } from '@r/components/kpi/skeleton/bar-chart-skeleton.tsx';
import { getDataBrick } from '../../components/data-brick.tsx';
import CountUp from 'react-countup';
import type { InsightPointInsightPointReadModel } from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useState } from 'react';
import { CompanyRankingList } from '@admin/components/company/company-ranking-list.tsx';
import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { KpiFilterBar } from '@r/components/kpi/filters/kpi-filter-bar.tsx';
import { companyStatuses } from '@r/types/index.ts';
import { getBarFillFromValue } from '@r/utils/chart-utils.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type PointInsights = InsightPointInsightPointReadModel;

export default function PointsInsights() {
  const { t } = useTranslation();

  let formattedPointsPerMonth: FormattedDataForRendering[] = [];
  let totalPeriodPoints: number | null = null;

  const [companyStatus, setCompanyStatusFilterValue] = useState<Option[]>([]);
  const [companyTypologies, setCompanyTypologiesFilterValue] = useState<Option[]>([]);
  const [selectedSalesIds, setSelectedSalesIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});

  const statusIds = companyStatus
    .map((status) => String(status.id))
    .filter((id): id is (typeof companyStatuses)[number] =>
      companyStatuses.includes(id as (typeof companyStatuses)[number])
    );

  const pointInsightsQuery = useApiAdmininsightspointsGetCollection({
    ...(statusIds.length > 0 ? { 'status[]': statusIds } : {}),
    ...(companyTypologies.length > 0
      ? { 'typology[]': companyTypologies.map((t) => String(t.id)) }
      : {}),
    ...(selectedSalesIds.length > 0 ? { 'sales[]': selectedSalesIds } : {}),
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  });
  useOrvalQueryErrorToast(pointInsightsQuery, t('toast.error.label'));

  const processPointsPerMonthResults = (
    pointInsights: PointInsights
  ): FormattedDataForRendering[] => {
    const totalPointsPerMonth = pointInsights.totalPointsPerMonth;

    return totalPointsPerMonth.map((item) => {
      const monthName = format(new Date(item.year, item.month - 1), 'MMMM', {
        locale: fr
      });
      const formatedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      const totalPoints = Number(item.value);

      return {
        date: `${formatedMonthName} ${item.year}`,
        data: totalPoints,
        fill: getBarFillFromValue(totalPoints)
      };
    });
  };

  const totalPeriodPointsResults = (pointInsights: PointInsights) => {
    const totalPointsPerMonth = pointInsights.totalPointsPerMonth;
    return totalPointsPerMonth.reduce((acc, item) => acc + Number(item.value), 0);
  };

  if (pointInsightsQuery.isSuccess && pointInsightsQuery.data) {
    formattedPointsPerMonth = processPointsPerMonthResults(pointInsightsQuery.data);
    totalPeriodPoints = totalPeriodPointsResults(pointInsightsQuery.data);
  }

  return (
    <>
      <KpiFilterBar
        onStatusChange={setCompanyStatusFilterValue}
        onTypologyChange={setCompanyTypologiesFilterValue}
        companyTypologies={companyTypologies}
        onSalesChange={setSelectedSalesIds}
        onDateRangeChange={(range) => {
          setDateRange(range);
        }}
        dateRange={dateRange}
      />

      <div className="flex flex-wrap space-y-5 grow xl:flex-nowrap xl:space-x-5 xl:space-y-0">
        <div className="flex flex-col space-y-5 w-full xl:w-6/12 xl:min-h-full">
          <div className="w-full">
            <Card className="flex h-[450px] flex-col">
              <CardHeader className="space-y-0">
                <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                  <CoinsIcon />

                  <div className="text-xl font-bold">
                    {t('admin.kpi.twelve.months.generated.points')}
                  </div>
                </CardTitle>

                {pointInsightsQuery.isSuccess && totalPeriodPoints !== null && (
                  <span>
                    <div className="mb-2 text-3xl font-bold text">
                      <CountUp
                        className="font-bold"
                        end={totalPeriodPoints}
                        duration={2}
                        suffix={` ${pointsTranslation(t, totalPeriodPoints)}`}
                        separator={' '}
                      />
                    </div>
                  </span>
                )}
              </CardHeader>

              <CardContent className="flex grow">
                {(pointInsightsQuery.isLoading || pointInsightsQuery.isError) && (
                  <BarChartSkeleton />
                )}

                {pointInsightsQuery.isSuccess && formattedPointsPerMonth.length > 0 && (
                  <BarChartBloc
                    formattedDataPerMonth={formattedPointsPerMonth}
                    dataType={'point'}
                  />
                )}
              </CardContent>

              <CardFooter className="justify-center text-sm font-light text-muted-foreground">
                {t('admin.kpi.twelve.months.generated.points.info')}
              </CardFooter>
            </Card>
          </div>

          <div className="w-full">
            {(pointInsightsQuery.isLoading || pointInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {pointInsightsQuery.isSuccess &&
              pointInsightsQuery.data &&
              pointInsightsQuery.data.avgAcquiredPoints !== null &&
              getDataBrick(
                t('admin.kpi.average.points.acquired'),
                <CoinsIcon />,
                pointInsightsQuery.data.avgAcquiredPoints,
                ` ${pointsTranslation(t, pointInsightsQuery.data.avgAcquiredPoints)}`,
                t('admin.kpi.average.points.acquired.info'),
                null,
                'large'
              )}
          </div>
        </div>

        <div className="w-full xl:w-6/12 xl:min-h-full">
          <CompanyRankingList
            className="xl:h-full"
            orderBy="balance"
            direction="desc"
            filters={{
              onlyCompaniesRegistered: statusIds.includes('company.status.registered'),
              companyTypologies: companyTypologies
            }}
            hideImportBtn={true}
            rankingValueType={'point'}
          />
        </div>
      </div>
    </>
  );
}
